import { useState, useEffect } from "react";
import XLSX from "xlsx";
import { SheetJSFT } from "./Types";
import "./loose-line-item.scss";
import ExportToExcel from "./Content";
import { useTranslation } from "react-i18next";
import Messages from "../../constants/Messages";
import "./FileUpload.scss";
import uploadIconBlack from "../../assets/images/uploadIconBlack.svg";
import downloadIcon from "../../assets/images/downloadIcon.svg";
import closeIcon from "../../assets/images/close-outline.svg";
import LoadingIndicator from "../LoadingIndicator";

const keyMap = {
    Pieces: "pieces",
    Length: "length",
    Width: "width",
    Height: "height",
    Weight: "weight",
    "Stackable (Yes/No)": "stackable",
    "Tiltable (Yes/No)": "turnable",
    "Weight Type (Per Item/Total)": "weightType",
    "Dimension Unit": "dimensionUnit",
    "Weight Unit": "weightUnit",
};

const mapToResponse = (data) =>
    data.map((row) => {
        const mapped = {};
        const packingType = [];

        Object.keys(row).forEach((key) => {
            const mappedKey = keyMap[key];
            packingType.push(mappedKey);

            if (["stackable", "turnable"].includes(mappedKey)) {
                mapped[mappedKey] = row[key] === "Y" || row[key] === "y";
                return;
            }

            if (["Pieces", "Length", "Width", "Height"].includes(key)) {
                mapped[mappedKey] = row[key] ? Math.round(row[key]) : row[key];
                return;
            }

            if (key === "Weight") {
                mapped[mappedKey] = row[key] ? Number(row[key]).toFixed(2) : row[key];
                return;
            }

            mapped[mappedKey] = row[key];
        });

        if (!packingType.includes("stackable")) {
            mapped.stackable = true;
        }

        if (!packingType.includes("turnable")) {
            mapped.turnable = true;
        }

        return mapped;
    });

const FileUpload = ({ onRead, pageName, closeButtonClick, needNegMargin }) => {
    const { t } = useTranslation(["labels", "errors"]);
    const [isLoading, setIsLoading] = useState(false);
    const [mappedResponse, setMappedResponse] = useState([]);
    const [weightCellState, setWeightCellState] = useState(undefined);
    const [dimensionCellState, setDimensionCellState] = useState(undefined);
    const [fileName, setFileName] = useState(undefined);
    const [error, setError] = useState("");

    const validatedataFunction = (parsedData) => {
        return parsedData.every((row) => Object.keys(row).length === 8);
    };

    const onCloseButtonClick = () => {
        setError("");
        setFileName(undefined);
        closeButtonClick();
    };

    const handleChange = (files) => {
        const f = files[0];
        // if (f.size > 2 * 1024 * 1024) {
        //     setError("errors.EXCEL_UPLOAD_FILE_TOO_LARGE");
        //     setIsLoading(false);
        //     return;
        // }
        const reader = new FileReader();

        setIsLoading(true); // show loader

        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const readedData = XLSX.read(data, { type: "binary" });
                const wsname = readedData.SheetNames[0];
                const ws = readedData.Sheets[wsname];
                const weightCell = ws["B1"] && ws["B1"].v;
                const dimensionCell = ws["B2"] && ws["B2"].v;
                const parsedData = XLSX.utils.sheet_to_json(ws, { range: 3 });

                const mapped = mapToResponse(parsedData);

                setMappedResponse(mapped);
                setWeightCellState(weightCell);
                setDimensionCellState(dimensionCell);
                setFileName(f?.name);

                const isValid = validatedataFunction(mapped);
                const isEmptyExcel = mapped?.length === 0;
                setError(isValid ? "" : Messages.EXCEL_UPLOAD_EMPTY_FIELDS);

                if (isValid) {
                    if (isEmptyExcel) {
                        setError(Messages.EXCEL_UPLOAD_EMPTY_FIELDS);
                    } else {
                        onRead(mapped, weightCell, dimensionCell);
                        closeButtonClick();
                    }
                }
            } catch (err) {
                console.error("Excel upload error:", err);
                setError(Messages.EXCEL_UPLOAD_EMPTY_FIELDS);
            } finally {
                setIsLoading(false);
            }
        };

        reader.readAsBinaryString(f);
    };

    useEffect(() => {
        if (fileName !== undefined) {
            setError("");
        }
    }, [fileName]);

    return (
        <>
            {isLoading && <LoadingIndicator />}
            <div className={`file-upload-section ${needNegMargin ? 'neg-margin' : ''}`}>
                <img
                    src={closeIcon}
                    alt="close"
                    title="close"
                    id="close-icon"
                    className="upload-close-icon"
                    onClick={onCloseButtonClick}
                />
                <div className="file-input-download my-2">
                    <input
                        type="file"
                        id="fileButton"
                        accept={SheetJSFT}
                        onChange={(e) => {
                            handleChange(e.target.files);
                            e.target.value = "";
                        }}
                    />
                    <img
                        src={uploadIconBlack}
                        alt="upload"
                        title="upload"
                        id="upload-icon"
                    />
                    <div className="upload-drop-label">
                        <label className="upload-label" htmlFor="fileButton">
                            {t('COMMON.UPLOAD_CLICK')}
                        </label>
                        <label className="drop-label">{t('COMMON.UPLOAD_DROP')}</label>
                    </div>
                    <label className="drop-label">
                        {/* {fileName || "Excel File (max 2 MB)"} */}
                         {fileName || t('COMMON.UPLOAD_DROP')}
                    </label>

                    <span className="search-panel__errormsg m-0">
                        {error && error !== "" ? t(error, { ns: "errors" }) : ""}
                    </span>
                </div>
                <div className="file-input-download my-2">
                    <img
                        src={downloadIcon}
                        alt="download"
                        title="download"
                        id="download-icon"
                    />
                    <ExportToExcel pageName={pageName}>Export</ExportToExcel>
                </div>
            </div>
        </>
    );
};

export default FileUpload;
